// Lib
import React, { MutableRefObject, useState } from 'react';
import classNames from 'classnames';
import { last } from 'lodash/fp';
import { connect, ConnectedProps } from 'react-redux';
import { CSSTransitionGroup } from '../../../../../../../../node_module_clones/react-transition-group';

// Utils
import { getConversationSubmissionsCount } from '../../state/aiToolUtils';
import {
    combineAssistantPromptMessage,
    constructMessagesFromPromptShortcuts,
    createUserPromptMessage,
} from '../../promptShortcutUtils';

// Selectors
import { hasAiHistory, isCurrentConversationStarted } from '../../state/aiToolStateSelector';

// Actions
import { modalOpened } from '../../../../../../../components/modal/modalActions';

// Components
import AiAssistantPopupPromptShortcutButton from './AiAssistantPopupPromptShortcutButton';
import LinkButton from '../../../../../../../components/buttons/LinkButton';
import AiAssistantPromptInput from './AiAssistantPromptInput';
import AiAssistantPromptStreaming from './AiAssistantPromptStreaming';
import AiModeHeaderSection from '../components/AiModeHeader';
import AiModeHeading from '../components/AiModeHeading';
import AiButton from '../components/AiButton';

// Types
import { ModalIds } from '../../../../../../../components/modal/modalConstants';
import { PromptShortcut } from '../../../../../../../../common/ai/aiPromptShortcutTypes';
import { AiConversationMessage } from '../../../../../../../../common/ai/aiConversationTypes';
import {
    AiPopupTransitionDirection,
    AiPromptMode,
    AiLocalAiConversation,
    AiResultType,
    UseAiToolState,
} from '../../state/aiToolStateTypes';
import { StreamRequestHandlers } from '../../state/useSubmitAiStreamRequestHandlers';

// Analytics
import { sendAmplitudeEvent } from '../../../../../../../analytics/amplitudeService';
import { intercomTrackEvent } from '../../../../../../../analytics/intercomService';
import { AMPLITUDE_USER_PROPS, TRACKED_FEATURES } from '../../../../../../../../common/analytics/statsConstants';
import { EVENT_TYPE_NAMES } from '../../../../../../../../common/analytics/amplitudeEventTypesUtil';

// Constants
import {
    AI_TRANSITION_GROUP_DURATION,
    AiSubmissionType,
    GENERATE_MORE_PROMPT_SHORTCUT,
} from '../../state/aiToolStateConstants';

// Styles
import './AiAssistantPopupPromptForm.scss';

/**
 * Gets the type for the "AI submission" analytics event.
 */
const getShortcutSubmissionType = (
    shortcut: PromptShortcut,
    currentConversation: AiLocalAiConversation,
): AiSubmissionType => {
    if (shortcut.getPromptMethod) return AiSubmissionType.BUILT_IN;
    if (shortcut.id === GENERATE_MORE_PROMPT_SHORTCUT.id) return AiSubmissionType.FOLLOW_UP;

    const conversationLength = getConversationSubmissionsCount(currentConversation);

    return conversationLength > 0 ? AiSubmissionType.FOLLOW_UP : AiSubmissionType.BUILT_IN;
};

const sendAiSubmissionAnalytics = (type: AiSubmissionType, conversationLength: number): void => {
    sendAmplitudeEvent({
        eventType: EVENT_TYPE_NAMES.AI_SUBMISSION,
        eventProperties: {
            type,
            conversationLength,
        },
        userProperties: {
            [AMPLITUDE_USER_PROPS.FEATURE]: { [TRACKED_FEATURES.AI]: true },
        },
    });

    intercomTrackEvent('ai-ideas-submission', {
        type,
        conversationLength,
    });
};

const sendAiPromptClickAnalytics = (type: AiSubmissionType, text?: string): void => {
    sendAmplitudeEvent({
        eventType: EVENT_TYPE_NAMES.USED_AI_SHORTCUT,
        eventProperties: {
            type,
            text,
        },
    });
};

// Determines if we have prompt shortcuts to show
const getHasPromptShortcuts = (promptShortcutsList: PromptShortcut[]) =>
    promptShortcutsList.filter(({ id }) => id !== GENERATE_MORE_PROMPT_SHORTCUT.id).length > 0;

const mapDispatchToProps = (dispatch: Function) => ({
    dispatchToReduxStore: dispatch, // connect raw dispatch, so it can be utilized in handleShortcutClick
    dispatchOpenReportAiFeedbackModal: (currentConversation: AiLocalAiConversation) =>
        dispatch(modalOpened(ModalIds.AI_REPORT_FEEDBACK, { currentConversation })),
});

const connector = connect(null, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;
export interface AiAssistantPopupPromptFormProps extends UseAiToolState, PropsFromRedux, StreamRequestHandlers {
    results: AiResultType[];
    fetching: boolean;
    fetched: boolean;
    isAiPopupMountedRef: MutableRefObject<boolean>;
}

const AiAssistantPopupPromptFormContent = (props: AiAssistantPopupPromptFormProps): React.ReactElement | null => {
    const {
        currentConversationId,
        currentConversation,
        results,
        fetching,

        chosenPromptShortcuts,
        addChosenPromptShortcut,
        removeChosenPromptShortcuts,
        resetToPreviousPromptShortcuts,
        dispatchToReduxStore,

        returnMode,
        setReturnMode,
        setTransitionDirection,

        setMode,
        selectNewConversation,

        dispatchOpenReportAiFeedbackModal,

        handlePromptSubmit,
        handleCancelStream,
        isAiPopupMountedRef,
    } = props;

    const [promptText, setPromptText] = useState('');

    const promptShortcut = fetching ? undefined : last(chosenPromptShortcuts);
    const promptShortcutsList = promptShortcut?.children || [];

    const isConversationStarted = isCurrentConversationStarted(props);

    const hasPromptShortcuts = getHasPromptShortcuts(promptShortcutsList);

    const returnToNewConversation = () => {
        setTransitionDirection(AiPopupTransitionDirection.DOWN);
        selectNewConversation();
    };

    const switchToHistoryMode = (transitionDirection: AiPopupTransitionDirection) => () => {
        sendAmplitudeEvent({ eventType: EVENT_TYPE_NAMES.VIEWED_AI_HISTORY });

        setTransitionDirection(transitionDirection);
        setMode(AiPromptMode.HISTORY);
        setReturnMode(AiPromptMode.HISTORY);
    };

    const returnToHistoryMode = () => {
        // Select the new conversation so that the history results get cleared
        selectNewConversation();
        switchToHistoryMode(AiPopupTransitionDirection.DOWN)();
    };

    const submitPromptMessages = async (messages: AiConversationMessage[]): Promise<void> => {
        setMode(AiPromptMode.PROMPTS);
        const combinedMessages = combineAssistantPromptMessage(messages);

        setTransitionDirection(AiPopupTransitionDirection.UP);

        handlePromptSubmit(combinedMessages);
        removeChosenPromptShortcuts(currentConversationId);
    };

    const submitForm = async (
        event?: React.FormEvent<HTMLFormElement> | React.KeyboardEvent<HTMLTextAreaElement>,
    ): Promise<void> => {
        event && event.preventDefault();

        const conversationLength = getConversationSubmissionsCount(currentConversation) + 1;
        sendAiSubmissionAnalytics(AiSubmissionType.CUSTOM, conversationLength);

        submitPromptMessages([
            ...constructMessagesFromPromptShortcuts(chosenPromptShortcuts, dispatchToReduxStore),
            createUserPromptMessage(promptText, {
                user: true,
                assistant: true,
            }),
        ]);
    };

    const handleShortcutClick = (shortcut: PromptShortcut): void => {
        setTransitionDirection(AiPopupTransitionDirection.UP);
        addChosenPromptShortcut(currentConversationId, shortcut);

        const submissionType = getShortcutSubmissionType(shortcut, currentConversation);
        const promptText = submissionType === AiSubmissionType.BUILT_IN ? shortcut.promptText : undefined;

        sendAiPromptClickAnalytics(submissionType, promptText);

        const isFinalPromptStep = shortcut.children.length === 0 && !shortcut.requireTextInput;
        if (isFinalPromptStep) {
            const conversationLength = getConversationSubmissionsCount(currentConversation) + 1;
            sendAiSubmissionAnalytics(submissionType, conversationLength);

            submitPromptMessages(
                constructMessagesFromPromptShortcuts([...chosenPromptShortcuts, shortcut], dispatchToReduxStore),
            );
        }
    };

    const onSubmitFeedback = () => {
        return currentConversation && dispatchOpenReportAiFeedbackModal(currentConversation);
    };

    const handleCancelClick = () => {
        setTransitionDirection(AiPopupTransitionDirection.DOWN);
        handleCancelStream();

        if (!hasPromptShortcuts) {
            resetToPreviousPromptShortcuts(currentConversationId);
        }
    };

    const showReportFeedbackButton = results.length > 0 && !currentConversation?.streamingResults;

    // if current conversation is fetching, return nothing — loader will be shown too quickly
    if (currentConversation.fetching) return null;

    // if current conversation is streaming, show the loading spinner w/ cancel button
    if (currentConversation?.streamingResults) {
        return <AiAssistantPromptStreaming handleCancelClick={handleCancelClick} />;
    }

    const hasHistory = hasAiHistory(props);

    return (
        <form
            className={classNames('AiAssistantPopupPromptFormContent', {
                streaming: currentConversation?.streamingResults,
                empty: results.length === 0,
            })}
            onSubmit={submitForm}
        >
            {isConversationStarted && (
                <AiModeHeaderSection>
                    <AiButton
                        iconName="ai-back"
                        onClick={returnMode === AiPromptMode.PROMPTS ? returnToNewConversation : returnToHistoryMode}
                    >
                        {returnMode === AiPromptMode.PROMPTS ? 'Start over' : 'Back'}
                    </AiButton>

                    {showReportFeedbackButton ? (
                        <LinkButton className="feedback-button" onClick={onSubmitFeedback}>
                            Leave feedback
                        </LinkButton>
                    ) : null}
                </AiModeHeaderSection>
            )}

            <div className="prompt-shortcuts-container">
                {promptShortcut?.question && <AiModeHeading>{promptShortcut.question}</AiModeHeading>}

                <ul className="AiButtonList prompt-shortcut-list">
                    {promptShortcutsList
                        .filter((shortcut) => shortcut.promptText)
                        .map((shortcut) => (
                            <li key={shortcut.id}>
                                <AiAssistantPopupPromptShortcutButton
                                    shortcut={shortcut}
                                    onClick={handleShortcutClick}
                                />
                            </li>
                        ))}
                </ul>
            </div>

            <div className="prompt-input-container">
                {!!promptShortcutsList.length && <h3 className="prompt-input-title">Something else…</h3>}

                <AiAssistantPromptInput
                    shouldDelayFocus={!!isAiPopupMountedRef.current}
                    promptText={promptText}
                    promptShortcut={promptShortcut}
                    setPromptText={setPromptText}
                    submitForm={submitForm}
                />
            </div>

            {!isConversationStarted && hasHistory && (
                <AiButton
                    className="history-button"
                    iconName="ai-history"
                    onClick={switchToHistoryMode(AiPopupTransitionDirection.UP)}
                >
                    History
                </AiButton>
            )}
        </form>
    );
};

const getPromptFormContentKey = (props: AiAssistantPopupPromptFormProps): string => {
    const { fetching, chosenPromptShortcuts, currentConversation } = props;

    const promptShortcut = fetching ? undefined : last(chosenPromptShortcuts);
    const promptShortcutId = promptShortcut?.id || 'default';

    if (!currentConversation?.fetched || currentConversation?.fetching) return 'loading';
    if (currentConversation?.streamingResults) return 'streaming';
    return promptShortcutId;
};

const AiAssistantPopupPromptForm = (props: AiAssistantPopupPromptFormProps): React.ReactElement => (
    <div className="AiAssistantPopupPromptForm">
        <CSSTransitionGroup
            transitionName={`fade-${props.transitionDirection}`}
            transitionEnterTimeout={AI_TRANSITION_GROUP_DURATION}
            transitionLeaveTimeout={AI_TRANSITION_GROUP_DURATION}
        >
            <AiAssistantPopupPromptFormContent {...props} key={getPromptFormContentKey(props)} />
        </CSSTransitionGroup>
    </div>
);

export default connector(AiAssistantPopupPromptForm);
